import React, { FC, memo, useContext, ReactNode, useMemo } from 'react'

import {
  ChargeVerifyBuyerDetails,
  PaymentRequestOptions,
  StoreVerifyBuyerDetails,
  TokenResult,
  VerifyBuyerResponseDetails,
} from '@square/web-sdk'
import { useFormContext } from 'react-hook-form'

import { AppContext } from 'Const'
import useEvent from 'Hooks/useEvent'

import SquarePaymentsForm from 'Components/Square/PaymentForm'

import { usePayment } from '../../Provider'

interface Props {
  children: ReactNode
  onPaymentError(error?: string): void
  onPaymentReady(): void
}

const PaymentForm: FC<Props> = ({ children, onPaymentError, onPaymentReady }) => {
  const { cart, shop, currentLocation } = useContext(AppContext)
  const { checkout, setTokenizing } = usePayment()
  const { currency } = shop
  const threeDSecure = shop.checkoutSetting?.threeDSecure
  const { setValue, setError } = useFormContext()

  const handleCardTokenizeResponseReceived = useEvent(
    (result: TokenResult, verifiedBuyer?: VerifyBuyerResponseDetails | null) => {
      setTokenizing(false)
      if (verifiedBuyer) {
        setValue('squarePayment.verificationToken', verifiedBuyer.token)
      }
      if (result.status == 'OK') {
        setValue('squarePayment.sourceId', result.token)
        void checkout()
      } else if (result.status == 'Error' || result.status == 'Invalid') {
        console.error(result.errors)
        result.errors?.forEach((error) => setError('squarePayment.sourceId', { message: error.message }))
      }
    },
  )

  const createPaymentRequest: PaymentRequestOptions = useMemo(
    () => ({
      countryCode: currentLocation.address.countryIsoCode,
      currencyCode: currency,
      total: {
        label: 'Total Money',
        amount: cart.totalMoney.toString(),
      },
    }),
    [cart.totalMoney, currency, currentLocation.address.countryIsoCode],
  )

  const createVerificationDetails: () => ChargeVerifyBuyerDetails | StoreVerifyBuyerDetails = useEvent(() => ({
    intent: 'CHARGE',
    currencyCode: currency,
    amount: cart.totalMoney.toString(),
    billingContact: {},
  }))

  return (
    <SquarePaymentsForm
      applicationId={shop.squareApplicationId}
      cardTokenizeResponseReceived={handleCardTokenizeResponseReceived}
      createPaymentRequest={createPaymentRequest}
      createVerificationDetails={threeDSecure ? createVerificationDetails : undefined}
      formProps={{ id: shop.id }}
      locationId={shop.squareLocationId}
      paymentFormError={onPaymentError}
      paymentFormReady={onPaymentReady}
    >
      {children}
    </SquarePaymentsForm>
  )
}

export default memo(PaymentForm)
